import { useEffect, useRef, useState } from 'react';

import { ChevronDown, ChevronRight, LogOut, Menu, Search, Settings2 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { CurrentUser } from '@/api/schema';
import { LOGIN_PATH } from '@/app/router';
import { breadcrumbFor } from '@/app/navigation';
import { Avatar } from '@/components/ui/Avatar';
import { useLogout } from '@/features/auth/api';
import { can } from '@/lib/permissions';

import { NotificationBell } from '@/features/notifications/components/NotificationBell';

import styles from './Topbar.module.css';

export function Topbar({ user, onOpenNav }: { user: CurrentUser; onOpenNav: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useLogout();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Closes on a click anywhere else, and on Escape (the same expectations any
  // dropdown carries).
  useEffect(() => {
    if (!menuOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const crumbs = breadcrumbFor(location.pathname);

  return (
    <header className={styles.topbar}>
      <button
        type="button"
        className={styles.burger}
        onClick={onOpenNav}
        aria-label="Open navigation"
      >
        <Menu size={18} />
      </button>

      <nav className={styles.crumbs} aria-label="Breadcrumb">
        {crumbs.map((crumb, index) => (
          <span key={crumb} className={styles.crumb}>
            {index > 0 ? <ChevronRight size={13} aria-hidden="true" /> : null}
            {index === crumbs.length - 1 ? (
              <b aria-current="page">{crumb}</b>
            ) : (
              <span>{crumb}</span>
            )}
          </span>
        ))}
      </nav>

      {/*
        Search and notifications are placeholders, as the plan has them for this
        phase. Disabled rather than merely inert: a control that looks live and does
        nothing is worse than one that says it is not ready. Search is Phase 8's
        deferred item; notifications arrive in Phase 26.
      */}
      <div className={styles.search}>
        <Search size={15} aria-hidden="true" className={styles.searchIcon} />
        <input
          className={styles.searchInput}
          placeholder="Search"
          disabled
          aria-label="Search (not available yet)"
        />
      </div>

      <NotificationBell />

      <div className={styles.userWrap} ref={menuRef}>
        <button
          type="button"
          className={styles.user}
          onClick={() => {
            setMenuOpen((open) => !open);
          }}
          // Named explicitly, because below 1000px `.userText` is display:none and the
          // only thing left inside is the avatar, whose image is alt="" and whose
          // initials are aria-hidden. Without this the button announced as "button"
          // with no name at all on a phone. The name is still in the label so the
          // control does not become anonymous to a screen reader on the desktop
          // layout, where sighted users can read it.
          aria-label={`Account menu for ${user.full_name}`}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
        >
          <Avatar name={user.full_name} size={32} />
          <span className={styles.userText}>
            <span className={styles.userName}>{user.full_name}</span>
            <span className={styles.userRole}>{user.role.name}</span>
          </span>
          <ChevronDown size={15} aria-hidden="true" className={styles.userChevron} />
        </button>

        {menuOpen ? (
          <div className={styles.menu} role="menu">
            <div className={styles.menuHead}>
              <p className={styles.menuName}>{user.full_name}</p>
              <p className={styles.menuEmail}>{user.email}</p>
            </div>

            {can(user, 'settings') ? (
              <button
                type="button"
                role="menuitem"
                className={styles.menuItem}
                onClick={() => {
                  setMenuOpen(false);
                  void navigate('/settings/users');
                }}
              >
                <Settings2 size={15} aria-hidden="true" /> Settings
              </button>
            ) : null}

            <div className={styles.menuSeparator} />

            <button
              type="button"
              role="menuitem"
              className={`${styles.menuItem} ${styles.menuItemDanger}`}
              onClick={() => {
                setMenuOpen(false);
                logout.mutate(undefined, {
                  onSettled: () => {
                    void navigate(LOGIN_PATH, { replace: true });
                  },
                });
              }}
            >
              <LogOut size={15} aria-hidden="true" /> Sign out
            </button>
          </div>
        ) : null}
      </div>
    </header>
  );
}
